import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import Tooltip from '@mui/material/Tooltip'
import React from 'react'

import OutlinedButton from 'shared/components/Buttons/OutlinedButton'
import GrayButton from 'shared/components/Buttons/GrayButton'

import { Box, useMediaQuery, useTheme } from '@mui/material'
import ContainedButton from 'shared/components/Buttons/ContainedButton'
import useLogin from 'shared/store/useLogin'
import { noInformationsWholesales, UserRoles } from 'shared/consts'
import { ButtonContainer } from './styles'

interface Props {
   status: CooperatingWholesalerStatus
   totalAvailability: Nullable<number>
   errorMessage: Nullable<string>
   isLoading: boolean
   wholesaleUuid: string
   wholesaleIndex: string
   productIndex: string
   currentWholesaleUuid: string
   netPrice: Nullable<number>
   onReportMissingProduct: () => void
   onSwapProduct: () => void
}

export default function Actions({
   onReportMissingProduct,
   onSwapProduct,
   status,

   wholesaleIndex,
   productIndex,
   wholesaleUuid,
   currentWholesaleUuid,
   netPrice,
   errorMessage,
   totalAvailability,
   isLoading,
}: Props) {
   const { t } = useTranslation('MAIN_CART')
   const theme = useTheme()
   const { user } = useLogin()
   const isMobile = useMediaQuery(theme.breakpoints.down('lg'))

   function renderConnectionProblemContent() {
      if (errorMessage && !errorMessage.includes('NOT_HANDLED')) {
         return (
            <Typography
               sx={{
                  fontWeight: 'bold',
                  color: 'red',
                  border: '1px solid red',
                  p: 1,
                  borderRadius: '5px',
                  maxWidth: isMobile ? '137px' : 'none',
               }}
               variant="body1"
            >
               {errorMessage}
            </Typography>
         )
      }
      return (
         <Typography
            sx={{
               fontWeight: 'bold',
               color: 'red',
               border: '1px solid red',
               p: 1,
               borderRadius: '5px',
               maxWidth: isMobile ? '137px' : 'none',
            }}
            variant="body1"
         >
            {t('swap.wholesaleReconnectFailed')}
         </Typography>
      )
   }

   if (
      status === 'ONLINE_CONNECTION_PROBLEM' ||
      status === 'PRODUCT_NOT_EXIST_IN_WHOLESALE' ||
      (status === 'OK_ONLINE_AVAILABILITY' && !netPrice)
   ) {
      return <ButtonContainer>{renderConnectionProblemContent()}</ButtonContainer>
   }
   if (wholesaleUuid === currentWholesaleUuid) {
      return (
         <Typography
            sx={{
               fontWeight: 'bold',
               color: theme.palette.success.dark,
               border: `1px solid ${theme.palette.success.dark}`,
               p: 1,
               borderRadius: '5px',
               maxWidth: isMobile ? '137px' : 'none',
            }}
            variant="body1"
         >
            {t('swap.inCart')}
         </Typography>
      )
   }
   if (!totalAvailability) {
      return (
         <ButtonContainer sx={{ flexDirection: 'column', gap: '8px' }}>
            <Tooltip
               placement="right"
               disableHoverListener={status !== 'OK_ONLINE_AVAILABILITY'}
               title={t('swap.sendReportTooltip') || ''}
            >
               <span>
                  <GrayButton
                     variant="outlined"
                     type="button"
                     onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        onReportMissingProduct()
                     }}
                     disabled={
                        status !== 'OK_ONLINE_AVAILABILITY' ||
                        isLoading ||
                        user.roles.includes(UserRoles.DEMO) ||
                        user.roles.includes(UserRoles.DEMO_SUBSCRIPTION)
                     }
                  >
                     {t('swap.reportButton')}
                  </GrayButton>
               </span>
            </Tooltip>
            {noInformationsWholesales[wholesaleUuid] && status !== 'OK_ONLINE_AVAILABILITY' && (
               <Box
                  sx={{
                     maxWidth: user.roles.includes(UserRoles.DATA_MANAGER) ? '400px' : 'none',
                     ml: user.roles.includes(UserRoles.DATA_MANAGER) ? 'auto' : '0',
                     mr: user.roles.includes(UserRoles.DATA_MANAGER) ? 'auto' : '0',
                     display: 'flex',
                     justifyContent: user.roles.includes(UserRoles.DATA_MANAGER) ? 'normal' : 'center',
                     alignItems: 'center',
                     mt: '8px',
                  }}
               >
                  <ContainedButton
                     fullWidth
                     sx={{
                        maxWidth: isMobile ? '100px' : '240px',
                     }}
                     onClick={() => {
                        const win = window.open('about:blank', '_blank')

                        if (!win) return
                        win.name = JSON.stringify({ check: true })
                        win.location.href = `https://pl.e-cat.intercars.eu/pl/Pełna-oferta/c/tecdoc?q=${
                           wholesaleIndex ?? productIndex
                        }%3Adefault-m%3AbranchAvailability%3AALL&sort=default-m`
                     }}
                  >
                     {t('checkAvailability')}
                  </ContainedButton>
               </Box>
            )}
         </ButtonContainer>
      )
   }
   return (
      <ButtonContainer>
         <OutlinedButton
            variant="outlined"
            onClick={(e) => {
               e.stopPropagation()
               e.preventDefault()
               onSwapProduct()
            }}
            disabled={status !== 'OK_ONLINE_AVAILABILITY' || isLoading}
         >
            {t('swap.swapButton')}
         </OutlinedButton>
      </ButtonContainer>
   )
}
